import React from "react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid,
} from "recharts";
import { BarChart2, Users, AlertCircle, CheckCircle2, Target, ClipboardList } from "lucide-react";
import { getPerformanceDashboard } from "../api";
import { useAuth } from "../context/AuthContext";
import { useTenantTimezone } from "../hooks/useTenantTimezone";
import { formatDate, tenantHour } from "../utils/dateFormat";
import { StatCard, QuickAction } from "../components/DashboardWidgets";

const NAVY = "#1e3a8a";
const TEAL = "#0f766e";
const AMBER = "#f59e0b";
const RED = "#dc2626";
const ROSE = "#be123c";

const GOAL_STATUS_COLORS: Record<string, string> = {
  met: "#16a34a", in_progress: NAVY, pending: AMBER, partial: AMBER, missed: RED,
};

const card: React.CSSProperties = {
  background: "white", borderRadius: 10, padding: "22px 24px",
  border: "1px solid #e2e8f0", boxShadow: "0 1px 3px rgba(0,0,0,0.06)",
};

function ProgressBar({ pct, color }: { pct: number; color: string }) {
  return (
    <div style={{ height: 6, borderRadius: 999, background: "#f1f5f9", overflow: "hidden" }}>
      <div style={{ width: `${Math.max(0, Math.min(100, pct))}%`, height: "100%", background: color, borderRadius: 999 }} />
    </div>
  );
}

// Performance module dashboard — role-aware: org view for admin/HR, a personal
// slice (reviews owed + own goals) for everyone else, which also covers
// managers via reviews where they are the reviewer.
// Spec: .claude/specs/module-dashboards-esign-performance.md
export default function PerformanceDashboardPage() {
  const { user } = useAuth();
  const tz = useTenantTimezone();
  const navigate = useNavigate();

  const { data } = useQuery({
    queryKey: ["dashboard-performance"],
    queryFn: getPerformanceDashboard,
    refetchInterval: 60_000,
  });

  const firstName = (user?.name || "").split(" ")[0];
  const hour = tenantHour(tz);
  const greeting = hour < 12 ? "Good morning" : hour < 17 ? "Good afternoon" : "Good evening";
  const dayStr = formatDate(new Date(), "long", tz);

  const header = (
    <div style={{ marginBottom: 28 }}>
      <h1 style={{ margin: 0, fontSize: 22, fontWeight: 800, color: "#0f172a", letterSpacing: -0.5 }}>
        {greeting}, {firstName} 👋
      </h1>
      <p style={{ margin: "4px 0 0", color: "#64748b", fontSize: 13 }}>
        {dayStr} · {data?.view === "personal" ? "Your reviews and goals" : "Review cycles at a glance"}
      </p>
    </div>
  );

  // ── Personal shape ──
  if (data?.view === "personal") {
    const needsInput: any[] = data.needs_your_input ?? [];
    const goals: any[] = data.my_goals ?? [];
    return (
      <div style={{ maxWidth: 900 }}>
        {header}
        <div style={{ ...card, marginBottom: 24 }}>
          <div style={{ fontSize: 15, fontWeight: 700, color: "#0f172a", marginBottom: 16 }}>Needs Your Input</div>
          {needsInput.length === 0 ? (
            <div style={{ textAlign: "center", padding: "36px 0", color: "#64748b", fontSize: 13 }}>
              No reviews waiting on you 🎉
            </div>
          ) : (
            needsInput.map((r, i) => (
              <div key={r.review_id + r.kind} role="button" tabIndex={0}
                onClick={() => navigate("/performance")}
                onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") navigate("/performance"); }}
                style={{
                  display: "flex", alignItems: "center", gap: 10, cursor: "pointer",
                  padding: "10px 0", borderBottom: i < needsInput.length - 1 ? "1px solid #f1f5f9" : "none",
                }}>
                <div style={{
                  width: 28, height: 28, borderRadius: 8, flexShrink: 0,
                  background: `${ROSE}15`, color: ROSE,
                  display: "flex", alignItems: "center", justifyContent: "center",
                }}>
                  <ClipboardList size={14} />
                </div>
                <div style={{ minWidth: 0, flex: 1 }}>
                  <div style={{ fontSize: 13, fontWeight: 600, color: "#334155" }}>
                    {r.kind === "self_assessment" ? "Your self-assessment" : `Review ${r.employee}`}
                  </div>
                  <div style={{ fontSize: 12, color: "#64748b", marginTop: 1 }}>
                    {r.cycle}{r.due_date ? ` · due ${formatDate(new Date(r.due_date), "short", tz)}` : ""}
                  </div>
                </div>
              </div>
            ))
          )}
        </div>

        <div style={{ ...card, marginBottom: 24 }}>
          <div style={{ fontSize: 15, fontWeight: 700, color: "#0f172a", marginBottom: 16 }}>My Goals</div>
          {goals.length === 0 ? (
            <div style={{ textAlign: "center", padding: "24px 0", color: "#64748b", fontSize: 13 }}>No goals yet</div>
          ) : (
            goals.map((g, i) => (
              <div key={g.goal_id} style={{ padding: "10px 0", borderBottom: i < goals.length - 1 ? "1px solid #f1f5f9" : "none" }}>
                <div style={{ display: "flex", justifyContent: "space-between", gap: 10, marginBottom: 6 }}>
                  <div style={{ fontSize: 13, fontWeight: 600, color: "#334155", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                    {g.title}
                  </div>
                  <span style={{ fontSize: 12, color: "#64748b", flexShrink: 0 }}>
                    {g.progress_pct}%{g.due_date ? ` · due ${formatDate(new Date(g.due_date), "short", tz)}` : ""}
                  </span>
                </div>
                <ProgressBar pct={g.progress_pct} color={GOAL_STATUS_COLORS[g.status] || NAVY} />
              </div>
            ))
          )}
        </div>

        <div style={{ display: "flex", gap: 10 }}>
          <QuickAction label="My Performance" icon={<BarChart2 size={14} />} to="/performance" color={ROSE} />
        </div>
      </div>
    );
  }

  // ── Org shape ──
  const stats = data?.stats;
  const cycles: any[] = data?.cycle_progress ?? [];
  const dist: any[] = data?.rating_distribution ?? [];
  const hasRatings = dist.some((d) => d.count > 0);
  const goalsSummary = data?.goals_summary;

  return (
    <div>
      {/* No width cap: the module's own list pages (Candidates, Jobs, Offers,
          Interview Hub) all run the full content width, so capping the module
          landing page made the layout jump as you moved between them. */}
      {header}

      <div style={{ display: "flex", flexWrap: "wrap", gap: 14, marginBottom: 28 }} className="stat-cards-row">
        <StatCard label="Active Cycles" value={stats?.active_cycles ?? 0} color={ROSE}
          icon={<BarChart2 size={18} />} to="/performance" subtitle="Running now" />
        <StatCard label="Reviews Pending" value={stats?.reviews_pending ?? 0} color={NAVY}
          icon={<ClipboardList size={18} />} to="/performance" subtitle="In active cycles" />
        <StatCard label="Reviews Overdue" value={stats?.reviews_overdue ?? 0}
          color={(stats?.reviews_overdue ?? 0) > 0 ? RED : TEAL}
          icon={<AlertCircle size={18} />} to="/performance"
          subtitle={(stats?.reviews_overdue ?? 0) > 0 ? "Past cycle due date" : "None overdue"} />
        <StatCard label="Completed" value={stats?.reviews_completed ?? 0} color={TEAL}
          icon={<CheckCircle2 size={18} />} to="/performance" subtitle="In active cycles" />
        <StatCard label="Goals In Progress" value={stats?.goals_in_progress ?? 0} color={AMBER}
          icon={<Target size={18} />} to="/performance" subtitle="Org-wide" />
      </div>

      <div style={{ ...card, padding: "20px 24px", marginBottom: 24 }}>
        <div style={{ fontSize: 15, fontWeight: 700, color: "#0f172a", marginBottom: 14 }}>Quick Actions</div>
        <div style={{ display: "flex", flexWrap: "wrap", gap: 10 }}>
          <QuickAction label="Review Cycles" icon={<BarChart2 size={14} />} to="/performance/cycles" color={ROSE} />
          <QuickAction label="Employees" icon={<Users size={14} />} to="/employees" color={NAVY} />
        </div>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 20, marginBottom: 24 }} className="grid-2col">
        <div style={card}>
          <div style={{ marginBottom: 16 }}>
            <div style={{ fontSize: 15, fontWeight: 700, color: "#0f172a" }}>Cycle Progress</div>
            <div style={{ fontSize: 12, color: "#64748b", marginTop: 2 }}>Active cycles · overdue first</div>
          </div>
          {cycles.length === 0 ? (
            <div style={{ textAlign: "center", padding: "40px 0", color: "#64748b", fontSize: 13 }}>No active cycles</div>
          ) : (
            cycles.map((c, i) => {
              const pct = c.total ? Math.round((c.completed / c.total) * 100) : 0;
              return (
                <div key={c.cycle_id} role="button" tabIndex={0}
                  onClick={() => navigate("/performance")}
                  onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") navigate("/performance"); }}
                  style={{ padding: "10px 0", cursor: "pointer", borderBottom: i < cycles.length - 1 ? "1px solid #f1f5f9" : "none" }}>
                  <div style={{ display: "flex", justifyContent: "space-between", gap: 10, marginBottom: 6 }}>
                    <div style={{ fontSize: 13, fontWeight: 600, color: "#334155", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                      {c.title}
                      {c.overdue && (
                        <span style={{ marginLeft: 8, fontSize: 11, fontWeight: 700, color: RED, background: "#fef2f2", padding: "2px 7px", borderRadius: 999 }}>
                          overdue
                        </span>
                      )}
                    </div>
                    <span style={{ fontSize: 12, color: "#64748b", flexShrink: 0 }}>
                      {c.completed}/{c.total}
                      {c.due_date ? ` · due ${formatDate(new Date(c.due_date), "short", tz)}` : ""}
                    </span>
                  </div>
                  <ProgressBar pct={pct} color={c.overdue ? RED : TEAL} />
                </div>
              );
            })
          )}
        </div>

        <div style={card}>
          <div style={{ marginBottom: 18 }}>
            <div style={{ fontSize: 15, fontWeight: 700, color: "#0f172a" }}>Rating Distribution</div>
            <div style={{ fontSize: 12, color: "#64748b", marginTop: 2 }}>
              {data?.rating_cycle ? `Final ratings · ${data.rating_cycle}` : "Final ratings · latest cycle"}
            </div>
          </div>
          {!hasRatings ? (
            <div style={{ textAlign: "center", padding: "60px 0", color: "#64748b", fontSize: 13 }}>No rated reviews yet</div>
          ) : (
            <ResponsiveContainer width="100%" height={180}>
              <BarChart data={dist} barSize={30}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" vertical={false} />
                <XAxis dataKey="rating" tick={{ fontSize: 11, fill: "#64748b" }} axisLine={false} tickLine={false} />
                <YAxis tick={{ fontSize: 11, fill: "#64748b" }} axisLine={false} tickLine={false} allowDecimals={false} width={24} />
                <Tooltip cursor={{ fill: "#f8fafc" }} formatter={(v: number) => [v, "Reviews"]} />
                <Bar dataKey="count" name="Reviews" fill={ROSE} radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          )}
        </div>
      </div>

      <div style={card}>
        <div style={{ fontSize: 15, fontWeight: 700, color: "#0f172a", marginBottom: 16 }}>Goals Summary</div>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(4, minmax(110px, 1fr))", gap: 12 }} className="table-wrap">
          {[
            { label: "Met", value: goalsSummary?.met ?? 0, color: "#16a34a" },
            { label: "In Progress", value: goalsSummary?.in_progress ?? 0, color: NAVY },
            { label: "Partially Met", value: goalsSummary?.partial ?? 0, color: AMBER },
            { label: "Missed", value: goalsSummary?.missed ?? 0, color: RED },
          ].map(({ label, value, color }) => (
            <div key={label} style={{ background: "#f8fafc", border: "1px solid #e2e8f0", borderRadius: 10, padding: "12px 14px" }}>
              <div style={{ fontSize: 20, fontWeight: 800, color }}>{value}</div>
              <div style={{ fontSize: 12, color: "#64748b", marginTop: 2 }}>{label}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
